import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { MessageSquare, Send, Phone, Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// SMS Send Dialog Component
export function SMSSendDialog({ 
  open, 
  onClose, 
  apiClient, 
  recipientName, 
  recipientPhone, 
  recipientType = 'customer', // 'customer' or 'employee'
  recipientId,
  contextType = null, // 'job', 'estimate', 'schedule', 'reminder'
  contextData = {}
}) {
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const [checkOptOut, setCheckOptOut] = useState(true);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const [loadingOptOut, setLoadingOptOut] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      if (recipientType === 'customer' && recipientId) {
        checkCustomerOptOut();
      }
    }
  }, [open, recipientId]);

  const fetchTemplates = async () => {
    try {
      const res = await apiClient.get('/sms/templates');
      setTemplates(res.data);
    } catch (err) {
      console.error('Failed to load SMS templates:', err);
    }
  };

  const checkCustomerOptOut = async () => {
    setLoadingOptOut(true);
    try {
      const res = await apiClient.get(`/customers/${recipientId}`);
      setIsOptedOut(res.data?.sms_opted_out || false);
    } catch (err) {
      console.error('Failed to check opt-out status:', err);
    }
    setLoadingOptOut(false);
  };

  const handleTemplateSelect = (templateType) => {
    setSelectedTemplate(templateType);
    const template = templates.find(t => t.notification_type === templateType);
    if (template) {
      let msg = template.message;
      // Replace template variables with context data
      msg = msg.replace('{customer_name}', recipientName || 'Customer');
      msg = msg.replace('{job_title}', contextData.jobTitle || 'your project');
      msg = msg.replace('{date}', contextData.date || 'the scheduled date');
      msg = msg.replace('{time}', contextData.time || '');
      msg = msg.replace('{estimate_url}', contextData.estimateUrl || '');
      msg = msg.replace('{invoice_number}', contextData.invoiceNumber || '');
      msg = msg.replace('{amount}', contextData.amount || '');
      setMessage(msg);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (!recipientPhone) {
      toast.error('No phone number available');
      return;
    }

    if (isOptedOut && checkOptOut) {
      toast.error('This customer has opted out of SMS notifications');
      return;
    }

    setSending(true);
    try {
      const endpoint = recipientType === 'customer' 
        ? `/customers/${recipientId}/send-sms`
        : `/employees/${recipientId}/send-sms`;
      
      await apiClient.post(endpoint, {
        phone_number: recipientPhone,
        message: message
      });
      
      toast.success(`SMS sent to ${recipientName}`);
      onClose();
      setMessage('');
      setSelectedTemplate('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send SMS');
    }
    setSending(false);
  };

  // Filter templates based on context
  const filteredTemplates = templates.filter(t => {
    if (contextType === 'job') {
      return ['job_started', 'job_completed', 'schedule_update'].includes(t.notification_type);
    }
    if (contextType === 'estimate') {
      return ['estimate_sent'].includes(t.notification_type);
    }
    if (contextType === 'schedule' || contextType === 'reminder') {
      return ['appointment_reminder', 'schedule_update'].includes(t.notification_type);
    }
    return true; // Show all templates if no context
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-500" />
            Send SMS to {recipientName}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            <span className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {recipientPhone || 'No phone number'}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Opt-out warning */}
          {isOptedOut && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Customer has opted out</p>
                <p className="text-red-400/70 text-sm">This customer has requested not to receive SMS messages.</p>
              </div>
            </div>
          )}

          {/* Opt-out check option */}
          {recipientType === 'customer' && !isOptedOut && (
            <div className="flex items-center gap-2">
              <Checkbox 
                id="check-optout" 
                checked={checkOptOut} 
                onCheckedChange={setCheckOptOut}
              />
              <Label htmlFor="check-optout" className="text-zinc-400 text-sm cursor-pointer">
                Respect customer opt-out preferences
              </Label>
            </div>
          )}

          {/* Template selector */}
          <div>
            <Label className="text-zinc-300">Quick Template</Label>
            <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                <SelectValue placeholder="Select a template (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="custom" className="text-zinc-300">Custom Message</SelectItem>
                {filteredTemplates.map(t => (
                  <SelectItem key={t.notification_type} value={t.notification_type} className="text-zinc-300">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message input */}
          <div>
            <Label className="text-zinc-300">Message</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[120px]"
              placeholder="Type your message..."
              maxLength={160}
            />
            <p className="text-zinc-500 text-xs mt-1 text-right">
              {message.length}/160 characters
            </p>
          </div>

          {/* Opt-out notice that will be added */}
          <p className="text-zinc-500 text-xs">
            Note: "Reply STOP to opt out" will be added if not already included.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-600">
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending || !message.trim() || !recipientPhone || (isOptedOut && checkOptOut)}
            className="bg-green-600 hover:bg-green-700"
          >
            {sending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send SMS
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// SMS History Component
export function SMSHistory({ customerId, apiClient }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [customerId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/customers/${customerId}/sms-history`);
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to load SMS history:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-400">No SMS messages yet</p>
        <p className="text-zinc-500 text-sm">Messages sent to this customer will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.map((sms) => (
        <Card key={sms.id} className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {sms.direction === 'incoming' ? (
                    <Badge variant="outline" className="border-blue-500 text-blue-400 text-xs">
                      Received
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-green-500 text-green-400 text-xs">
                      Sent
                    </Badge>
                  )}
                  {sms.status === 'sent' || sms.status === 'delivered' ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : sms.status === 'failed' ? (
                    <XCircle className="w-4 h-4 text-red-500" />
                  ) : null}
                  {sms.notification_type && sms.notification_type !== 'direct' && (
                    <Badge className="bg-zinc-700 text-zinc-300 text-xs">
                      {sms.notification_type.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>
                <p className="text-white text-sm">{sms.message}</p>
                {sms.error && (
                  <p className="text-red-400 text-xs mt-1">Error: {sms.error}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-zinc-500 text-xs">
                  {sms.direction === 'incoming' ? sms.from_phone : sms.to_phone}
                </p>
                <p className="text-zinc-600 text-xs flex items-center gap-1 justify-end">
                  <Clock className="w-3 h-3" />
                  {new Date(sms.sent_at || sms.received_at).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// SMS Tab for Customer Detail Page
export function SMSTab({ customerId, customerName, customerPhone, apiClient }) {
  const [showSendDialog, setShowSendDialog] = useState(false);

  return (
    <div className="space-y-4">
      {/* Header with Send SMS button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-green-500" />
          SMS Messages
        </h3>
        <Button 
          onClick={() => setShowSendDialog(true)}
          className="bg-green-600 hover:bg-green-700"
          disabled={!customerPhone}
        >
          <Send className="w-4 h-4 mr-2" />
          Send SMS
        </Button>
      </div>

      {!customerPhone && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <p className="text-yellow-400 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            No phone number on file. Add a phone number to send SMS messages.
          </p>
        </div>
      )}

      {/* SMS History */}
      <SMSHistory customerId={customerId} apiClient={apiClient} />

      {/* Send Dialog */}
      <SMSSendDialog
        open={showSendDialog}
        onClose={() => setShowSendDialog(false)}
        apiClient={apiClient}
        recipientName={customerName}
        recipientPhone={customerPhone}
        recipientType="customer"
        recipientId={customerId}
      />
    </div>
  );
}

// Quick SMS Button for use in dropdowns/menus
export function QuickSMSButton({ 
  apiClient, 
  recipientName, 
  recipientPhone, 
  recipientType, 
  recipientId, 
  contextType, 
  contextData,
  variant = "ghost",
  size = "sm",
  className = ""
}) {
  const [showDialog, setShowDialog] = useState(false);

  return (
    <>
      <Button 
        variant={variant} 
        size={size}
        onClick={() => setShowDialog(true)}
        className={`text-green-400 hover:text-green-300 ${className}`}
        disabled={!recipientPhone}
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        Send SMS
      </Button>
      
      <SMSSendDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        apiClient={apiClient}
        recipientName={recipientName}
        recipientPhone={recipientPhone}
        recipientType={recipientType}
        recipientId={recipientId}
        contextType={contextType}
        contextData={contextData}
      />
    </>
  );
}

// SMS Inbox Component for Admin Dashboard
export function SMSInbox({ apiClient, limit = 20 }) {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [conversation, setConversation] = useState([]);

  useEffect(() => {
    fetchInbox();
  }, []);

  const fetchInbox = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/sms/inbox?limit=${limit}`);
      setMessages(res.data.messages || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to load SMS inbox:', err);
    }
    setLoading(false);
  };

  const markAsRead = async (messageId) => {
    try {
      await apiClient.put(`/sms/inbox/${messageId}/read`);
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;
    
    setSending(true);
    try {
      await apiClient.post(`/sms/inbox/${selectedMessage.id}/reply`, {
        message: replyText
      });
      toast.success('Reply sent!');
      setReplyText('');
      setSelectedMessage(null);
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    }
    setSending(false);
  };

  const loadConversation = async (phone) => {
    try {
      const res = await apiClient.get(`/sms/conversation/${encodeURIComponent(phone)}`);
      setConversation(res.data.messages || []);
      setShowConversation(true);
    } catch (err) {
      toast.error('Failed to load conversation');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-green-500" />
          SMS Inbox
          {unreadCount > 0 && (
            <Badge className="bg-green-500 text-white">{unreadCount} new</Badge>
          )}
        </h3>
        <Button variant="outline" size="sm" onClick={fetchInbox} className="border-zinc-600">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Messages List */}
      {messages.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No incoming messages yet</p>
          <p className="text-zinc-500 text-sm">Messages from customers and team will appear here</p>
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map((msg) => (
            <Card 
              key={msg.id} 
              className={`bg-zinc-800/50 border-zinc-700 cursor-pointer hover:bg-zinc-800 transition-colors ${!msg.read ? 'border-l-4 border-l-green-500' : ''}`}
              onClick={() => {
                setSelectedMessage(msg);
                if (!msg.read) markAsRead(msg.id);
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {msg.sender_type === 'customer' ? (
                        <Badge variant="outline" className="border-blue-500 text-blue-400 text-xs">Customer</Badge>
                      ) : msg.sender_type === 'employee' ? (
                        <Badge variant="outline" className="border-purple-500 text-purple-400 text-xs">Employee</Badge>
                      ) : (
                        <Badge variant="outline" className="border-zinc-500 text-zinc-400 text-xs">Unknown</Badge>
                      )}
                      {!msg.read && (
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      )}
                      {msg.replied && (
                        <CheckCircle className="w-4 h-4 text-green-500" title="Replied" />
                      )}
                    </div>
                    <p className="font-medium text-white truncate">
                      {msg.sender_name || msg.from_phone}
                    </p>
                    <p className="text-zinc-400 text-sm truncate">{msg.message}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-zinc-500 text-xs">
                      <Phone className="w-3 h-3 inline mr-1" />
                      {msg.from_phone}
                    </p>
                    <p className="text-zinc-600 text-xs flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" />
                      {new Date(msg.received_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reply Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={(open) => !open && setSelectedMessage(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-500" />
              Message from {selectedMessage?.sender_name || selectedMessage?.from_phone}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {selectedMessage?.from_phone}
                {selectedMessage?.sender_type && (
                  <Badge variant="outline" className="text-xs">
                    {selectedMessage.sender_type}
                  </Badge>
                )}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Original Message */}
            <div className="bg-zinc-800 rounded-lg p-4">
              <p className="text-xs text-zinc-500 mb-2">
                Received {selectedMessage && new Date(selectedMessage.received_at).toLocaleString()}
              </p>
              <p className="text-white">{selectedMessage?.message}</p>
            </div>

            {/* View Conversation Button */}
            <Button 
              variant="outline" 
              size="sm"
              className="border-zinc-600"
              onClick={() => selectedMessage && loadConversation(selectedMessage.from_phone)}
            >
              <Clock className="w-4 h-4 mr-2" />
              View Full Conversation
            </Button>

            {/* Reply Input */}
            <div>
              <Label className="text-zinc-300">Reply</Label>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[100px]"
                placeholder="Type your reply..."
                maxLength={160}
              />
              <p className="text-zinc-500 text-xs mt-1 text-right">
                {replyText.length}/160 characters
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedMessage(null)} className="border-zinc-600">
              Close
            </Button>
            <Button 
              onClick={handleReply}
              disabled={sending || !replyText.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {sending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reply
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conversation Dialog */}
      <Dialog open={showConversation} onOpenChange={setShowConversation}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white">Conversation History</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {conversation.map((msg, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg ${
                  msg.direction === 'incoming' 
                    ? 'bg-zinc-800 mr-8' 
                    : 'bg-green-600/20 ml-8 border border-green-600/30'
                }`}
              >
                <p className="text-white text-sm">{msg.message}</p>
                <p className="text-zinc-500 text-xs mt-1">
                  {new Date(msg.sent_at || msg.received_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SMSTab;
